import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import { Locales, noticeError, trackHeapEvent, Config } from '@ratehub/base-ui';
import { MortgageDocument } from '@ratehub/documents/mtg-journey-document/stable';
import { calculateMortgageTotal, isCanwiseProvider } from '@ratehub/mtg-common';
import { getSchemaVersionFromDocument } from '@ratehub/documents/common/v1';


dayjs.extend(isSameOrAfter);

/**
 *
 * @param documents
 * @param locale
 * @returns filtered and sorted rows
 */
function extractMortgageRows(documents, locale) {
    return documents
        .reduce(extractValidMortgageDocuments, []) // convert to MST so we can use getters, etc.
        .filter(filterDeletedDocuments)
        .filter(filterMortgageDocumentsWithoutEulaOrCanwiseInquiry)
        .sort(sortDocumentsByLastEditDate)
        .map(extractMortgageRow(locale));
}

// Support soft-delete functionality.
// TODO: If we are able to update metadata on get-session request, we can remove this.
// This is a hopefully temporary solution to filter out soft-deleted documents that don't have updated metadata; it's only updated on save ATM.
function filterDeletedDocuments(document) {
    return document.journey.application.metadata.deleted !== true;
}

function extractValidMortgageDocuments(documents, snapshot) {
    try {
        const document = MortgageDocument.create(snapshot);

        documents.push(document);
    } catch (originalError) {
        const logging = {
            originalError: originalError.message,
            documentID: snapshot.id,
        };
        
        noticeError(originalError, {
            message: '[extractMortgageRows]: Error creating mortgage document from snapshot',
            ...logging,
        });

        trackHeapEvent('Failed to load dashboard document', { originalError, ...logging });
    }

    return documents;
}

function sortDocumentsByLastEditDate(a, b) {
    const aLastModified = dayjs(a.journey.application.metadata.lastEditDate);
    const bLastModified = dayjs(b.journey.application.metadata.lastEditDate);

    return aLastModified.isSameOrAfter(bLastModified)
        ? -1
        : 1;
}

function filterMortgageDocumentsWithoutEulaOrCanwiseInquiry(document) {
    if (document.journey.agreedPrivacyPolicy) {
        return true; // EULA is signed
    }

    return document.journey.inquiries
        // Need to format like this to pass to isCanwiseProvider
        .map(inquiry => ({
            ...(inquiry.rate ?? {}),
            provider: {
                ...(inquiry.provider ?? {}),
                brokerage: inquiry.brokerage,
            },
        }))
        .some(isCanwiseProvider);
}

function extractMortgageRow(locale) {
    return (document, index) => {
        const hasEula = document.journey.agreedPrivacyPolicy;
        const hasAgent = !!document?.journey?.application?.agent?.calendarID;
        const primaryContact = document.journey.primaryContact;
        // TODO: Cleanup when v4 finalized
        const purpose = getSchemaVersionFromDocument(document) === 4
            ? document.journey.purpose
            : document.journey.purpose.id;

        return {
            key: document.id,
            document: {
                id: document.id,
                address: document.journey.subjectProperty.address,
                type: purpose,
                amount: calculateMortgageTotal({
                    scenarioType: document.journey.transactionType,
                    purchasePrice: document.journey.mortgage.purchasePrice,
                    downPayment: document.journey.mortgage.downPayment,
                    termInMonths: document.journey.mortgage.term,
                    mortgageBalance: document.journey.subjectProperty.existingMortgage?.currentMortgageBalance,
                    additionalFunds: document.journey.mortgage.additionalFundsRequired,
                    creditLimit: document.journey.mortgage.additionalFundsRequired,
                }),
                modified: document.journey.application.metadata.lastEditDate,
                resumeHREF: getActionableApplicationHREF(document.id, primaryContact.email, locale),
                // requirement: don't show these buttons if the EULA was not accepted
                get bookAgentHREF() {
                    return hasEula && hasAgent
                        ? getActionableApplicationHREF(document.id, primaryContact.email, locale, '/appointment-booking')
                        : undefined;
                },
                get uploadDocsHREF() {
                    return hasEula
                        ? getActionableApplicationHREF(document.id, primaryContact.email, locale, '/document-upload')
                        : undefined;
                },
                // TODO: Remove these when no longer needed
                get city() { return document.journey.subjectProperty.address.city; },
                get province() { return document.journey.subjectProperty.address.province; },
            },
            isMostRecent: index === 0,
        };
    };
}

/**
 * @param {string} leadID: Document ID
 * @param {string} email
 * @param {string} locale: "en-CA", "fr-CA",
 * @param {string} additionalFragment
 * @returns {string, null}
 *          String - actionable HREF
 *          Null - if leadID or email not passed
 */
function getActionableApplicationHREF(leadID, email, locale, additionalFragment = ''){
    const fragment = locale === Locales.FRENCH
        ? `hypotheque/appliquez${additionalFragment}`
        : `mortgages/apply${additionalFragment}`;

    return `${Config.RATEHUB_BASE_URL}/${fragment}?leadID=${leadID}&documentID=${leadID}&email=${encodeURIComponent(email)}`;
}

export default extractMortgageRows;
